// src/pages/.../EstoqueGenerico.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Table from "../../../../../components/TableV2";
import TitleButtons from "../../../../../components/TitleButtons";
import { generatePdfReport } from "../../../../../utils/pdf/generatePdfReport";
import { numeroMonetarioParaExtenso } from "../../../../../utils/numeroPorExtenso";
import { useAuth } from "../../../../../context/auth-context";
import ProdutoDetailSidePanel from "../PRODUTOS/ProdutoDetailSidePanel";

/** ---------------------- Tipos ---------------------- */

type EstoqueConfig = {
  areaSlug: string;
  estoqueSlug: string;
  title: string;
  reportTitle: string;
  fileNamePrefix: string;
  movimentacaoPath: string;
  detalheProdutoPath: (codigo: string) => string;
};

export type ModuloAtual = {
  id: number;
  codigo: string;
  label: string;
};

type MargemRaw = {
  margem_percentual?: number | string | null;
  definido_por_id?: number | string | null;
  data_definicao?: string | null;
};

export type EstoqueProduto = {
  produtoId: number | null;
  estoqueId: number | null;
  moduloId: number | null;

  posicao: string | null;
  codigo: string;
  ncm: string | null;
  nomeProduto: string;
  custoUnitario: string | null;
  quantidade: number | null;
  classificacao: string | null;
  moeda: string | null;

  margemPercentual: string;
  vendavel: boolean | string | null;

  estoqueCodigo: string | null;
  estoqueNome: string | null;
  moduloCodigo: string | null;
  moduloLabel: string | null;

  ocultar?: boolean;
};

export type EstoqueRowAPI = {
  produto_id?: number | string | null;
  estoque_id?: number | string | null;
  modulo_id?: number | string | null;

  codigo: string;
  nome: string;
  classificacao: string | null;
  posicao: string | null;
  quantidade_atual: number | string | null;
  custo_unitario_atual: string | number | null;
  ultimo_custo_unitario?: string | number | null;
  moeda?: string | null;
  ultima_moeda?: string | null;
  ncm?: string | null;

  vendavel?: boolean | string | null;
  margem_percentual?: number | string | null;
  margem_percentual_atual?: number | string | null;
  margens?: MargemRaw[];

  estoque_codigo?: string | null;
  estoque_nome?: string | null;
  modulo_codigo?: string | null;
  modulo_label?: string | null;

  ocultar?: boolean;
};

type ListaResp = {
  ok?: boolean;
  fonte?: "cache" | "banco";
  versao?: number | null;
  estoque?: {
    id?: number | string;
    codigo?: string;
    nome?: string;
    modulo_id?: number | string;
  };
  modulo?: {
    id?: number | string;
    codigo?: string;
    label?: string;
  };
  resumo?: {
    total_produtos?: number | string;
    quantidade_total?: number | string;
    valor_total_estimado?: number | string;
  };
  data: EstoqueRowAPI[];
  visiveis?: EstoqueRowAPI[];
  ocultos?: EstoqueRowAPI[];
};

/** ---------------------- Constantes API ---------------------- */

const API_LOGVI_BASE = "https://api.logvi-havox.com";

const URL_ESTOQUE = `${API_LOGVI_BASE}/template/estoque-administrativo/get-estoque`;
const ACCESS_TOKEN_KEY = "access_token";

const POLL_FALLBACK_MS = 15000;

const TABLET_BREAKPOINT = 900;
const MOBILE_BREAKPOINT = 640;
const SMALL_MOBILE_BREAKPOINT = 420;

/** ---------------------- Config / rota ---------------------- */

const ESTOQUE_SLUG_TO_MODULO_CODIGO: Record<string, string> = {
  "estoque-p-servico-e-comercial": "suprimentos-estoque",
  "estoque-p-servico-campos": "suprimentos-estoque-campos",
  "estoque-p-uso-coorporativo": "rh-estoque",
  "estoque-p-tecnicos-e-laboratorio": "tec-estoque",
  "materiais-p-venda": "suprimentos-estoque",
  "coordenacao-rastreio-de-materiais": "coordenacao",
};

function normalizeSlug(value: unknown): string {
  return String(value ?? "")
    .trim()
    .replace(/^\/+|\/+$/g, "")
    .toLowerCase();
}

function normalizeComparable(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/_/g, "-")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function slugToTitle(slug: string): string {
  return String(slug || "")
    .split("-")
    .filter(Boolean)
    .map((part) => {
      if (part.toLowerCase() === "p") return "p/";
      if (part.toLowerCase() === "e") return "e";
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(" ")
    .replace(/\s+p\/\s+/g, " p/ ");
}

function getConfigByParams(
  areaSlug?: string,
  estoqueSlug?: string
): EstoqueConfig {
  const area = normalizeSlug(areaSlug) || "suprimentos";
  const estoque = normalizeSlug(estoqueSlug) || "estoque";

  const titleFromSlug = slugToTitle(estoque);

  return {
    areaSlug: area,
    estoqueSlug: estoque,
    title: `${slugToTitle(area)} - ${titleFromSlug}`,
    reportTitle: `Relatório de Estoque - ${titleFromSlug}`,
    fileNamePrefix: `${area}-${estoque}-Estoque`,
    movimentacaoPath: `/${area}/${estoque}/estoque/realizar-movimentacao`,
    detalheProdutoPath: (codigo) =>
      `/${area}/${estoque}/produtos/${encodeURIComponent(codigo)}`,
  };
}

function findModuloAtualFromSessao(
  sessao: any,
  config: EstoqueConfig
): ModuloAtual | null {
  const areaSlug = normalizeComparable(config.areaSlug);
  const estoqueSlug = normalizeComparable(config.estoqueSlug);

  const expectedModuloCodigo =
    ESTOQUE_SLUG_TO_MODULO_CODIGO[estoqueSlug] || estoqueSlug;

  const enderecos = Array.isArray(sessao?.enderecos_com_modulos)
    ? sessao.enderecos_com_modulos
    : [];

  const enderecoSelecionadoId =
    sessao?.endereco_selecionado_id !== undefined &&
    sessao?.endereco_selecionado_id !== null
      ? String(sessao.endereco_selecionado_id)
      : null;

  const enderecosOrdenados = [
    ...enderecos.filter((e: any) =>
      enderecoSelecionadoId ? String(e?.id) === enderecoSelecionadoId : false
    ),
    ...enderecos.filter((e: any) =>
      enderecoSelecionadoId ? String(e?.id) !== enderecoSelecionadoId : true
    ),
  ];

  for (const endereco of enderecosOrdenados) {
    const departamentos = Array.isArray(endereco?.departamentos_acessiveis)
      ? endereco.departamentos_acessiveis
      : [];

    for (const dep of departamentos) {
      const depSlug = normalizeComparable(dep?.slug ?? dep?.routeKey);

      if (areaSlug && depSlug && depSlug !== areaSlug) {
        continue;
      }

      const modulos = Array.isArray(dep?.modulos) ? dep.modulos : [];

      const foundByCodigo = modulos.find((mod: any) => {
        const modCodigo = normalizeComparable(mod?.codigo);
        return modCodigo === normalizeComparable(expectedModuloCodigo);
      });

      const found =
        foundByCodigo ||
        modulos.find((mod: any) => {
          const modCodigo = normalizeComparable(mod?.codigo);
          const modLabel = normalizeComparable(mod?.label);

          return (
            modCodigo === estoqueSlug ||
            modLabel === estoqueSlug ||
            modCodigo.includes(estoqueSlug) ||
            estoqueSlug.includes(modCodigo)
          );
        });

      if (found?.id !== undefined && found?.id !== null) {
        const id = Number(found.id);

        if (Number.isInteger(id) && id > 0) {
          return {
            id,
            codigo: String(found.codigo || ""),
            label: String(found.label || ""),
          };
        }
      }
    }
  }

  return null;
}

/** ---------------------- Utils ---------------------- */

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function getAccessToken(): string | null {
  try {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

function buildAuthHeaders(includeContentType = false): Record<string, string> {
  const token = getAccessToken();

  const headers: Record<string, string> = {
    Accept: "application/json",
  };

  if (includeContentType) {
    headers["Content-Type"] = "application/json";
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

function toNumberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;

  const n =
    typeof value === "number" ? value : Number(String(value).replace(",", "."));

  return Number.isFinite(n) ? n : null;
}

function normalizeText(value: unknown): string {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function normalizeCurrency(value: unknown): string | null {
  const moeda = String(value ?? "")
    .trim()
    .toUpperCase();
  return moeda ? moeda : null;
}

function moedaFromItem(p: EstoqueProduto): string | null {
  return p?.moeda && String(p.moeda).trim() !== ""
    ? String(p.moeda).trim().toUpperCase()
    : null;
}

function moedaSymbol(moeda?: string | null): string {
  const normalized = normalizeCurrency(moeda);

  if (normalized === "USD") return "$";
  if (normalized === "EUR") return "€";
  if (normalized === "BRL") return "R$";

  return "";
}

function formatCurrency(
  valor: string | number | null | undefined,
  moeda?: string | null
): string {
  if (!moeda) return "N/A";

  const n =
    typeof valor === "string" ? Number(valor.replace(",", ".")) : valor ?? 0;

  if (!Number.isFinite(n)) return "N/A";

  const symbol = moedaSymbol(moeda);
  if (!symbol) return "N/A";

  return `${symbol} ${n.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatMoneyByCurrency(
  value: string | number | null | undefined,
  moeda?: string | null
): string {
  const n =
    typeof value === "string" ? Number(value.replace(",", ".")) : value ?? NaN;

  if (!Number.isFinite(n)) return "N/A";

  const num = n.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const symbol = moedaSymbol(moeda);

  return symbol ? `${symbol} ${num}` : num;
}

function formatPercent(value: unknown): string {
  const n = Number(String(value ?? "0").replace(",", "."));

  if (!Number.isFinite(n)) return "0";

  return n.toLocaleString("pt-BR", {
    maximumFractionDigits: 2,
  });
}

function pickLatestMargin(row: EstoqueRowAPI): string {
  const direct =
    row.margem_percentual_atual ??
    row.margem_percentual ??
    row.margens?.[0]?.margem_percentual ??
    null;

  if (direct === null || direct === undefined || direct === "") {
    return "0";
  }

  const n = Number(String(direct).replace(",", "."));

  return Number.isFinite(n) ? String(n) : String(direct);
}

function vendavelToLabel(value: unknown): string {
  if (typeof value === "string") {
    const normalized = normalizeText(value);

    if (normalized === "sim" || normalized === "true" || normalized === "1") {
      return "Sim";
    }

    if (
      normalized === "nao" ||
      normalized === "não" ||
      normalized === "false" ||
      normalized === "0"
    ) {
      return "Não";
    }

    if (!normalized) return "N/A";

    return value;
  }

  if (value === true) return "Sim";
  if (value === false) return "Não";

  return "N/A";
}

function isVendavelSim(value: unknown): boolean {
  if (typeof value === "string") {
    const normalized = normalizeText(value);
    return normalized === "sim" || normalized === "true" || normalized === "1";
  }

  return value === true;
}

function isVendavelNao(value: unknown): boolean {
  if (typeof value === "string") {
    const normalized = normalizeText(value);
    return (
      normalized === "nao" ||
      normalized === "não" ||
      normalized === "false" ||
      normalized === "0"
    );
  }

  return value === false;
}

function normalizeRow(r: EstoqueRowAPI): EstoqueProduto {
  const custo = r.custo_unitario_atual ?? r.ultimo_custo_unitario ?? null;

  const moeda =
    normalizeCurrency(r.moeda) ?? normalizeCurrency(r.ultima_moeda) ?? null;

  return {
    produtoId: toNumberOrNull(r.produto_id),
    estoqueId: toNumberOrNull(r.estoque_id),
    moduloId: toNumberOrNull(r.modulo_id),

    posicao: r.posicao ?? null,
    codigo: String(r.codigo ?? ""),
    ncm: r.ncm ?? null,
    nomeProduto: String(r.nome ?? ""),
    custoUnitario: custo === null || custo === undefined ? null : String(custo),
    quantidade: toNumberOrNull(r.quantidade_atual),
    classificacao: r.classificacao ?? null,
    moeda,

    margemPercentual: pickLatestMargin(r),
    vendavel: r.vendavel ?? null,

    estoqueCodigo: r.estoque_codigo ?? null,
    estoqueNome: r.estoque_nome ?? null,
    moduloCodigo: r.modulo_codigo ?? null,
    moduloLabel: r.modulo_label ?? null,

    ocultar: Boolean(r.ocultar),
  };
}

function sameArray(a: EstoqueProduto[], b: EstoqueProduto[]): boolean {
  if (a.length !== b.length) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

function buildRow(p: EstoqueProduto): string[] {
  const moeda = moedaFromItem(p);

  return [
    p.posicao ?? "",
    p.codigo ?? "",
    p.ncm ?? "",
    p.nomeProduto ?? "",
    formatCurrency(p.custoUnitario, moeda),
    moeda ?? "N/A",
    String(p.quantidade ?? 0),
    p.classificacao ?? "",
  ];
}

function buildDiretoriaRow(p: EstoqueProduto): string[] {
  const moeda = moedaFromItem(p);
  const margem = Number(String(p.margemPercentual ?? "0").replace(",", "."));
  const custo = Number(String(p.custoUnitario ?? "").replace(",", "."));

  let precoVenda = "pendente";

  if (Number.isFinite(custo) && Number.isFinite(margem) && margem !== 0) {
    precoVenda = formatMoneyByCurrency(custo * (1 + margem / 100), moeda);
  }

  return [
    p.codigo ?? "",
    p.nomeProduto ?? "",
    String(p.quantidade ?? 0),
    formatMoneyByCurrency(p.custoUnitario, moeda),
    moeda ?? "—",
    formatPercent(p.margemPercentual),
    precoVenda,
    vendavelToLabel(p.vendavel),
    p.classificacao ?? "",
    p.posicao ?? "",
  ];
}

function rowMatchesFilters(
  row: (string | number | React.ReactNode)[],
  filters: string[]
) {
  return row.every((cell, i) => {
    const filtro = normalizeText(filters[i] ?? "");
    if (!filtro) return true;

    const valor =
      typeof cell === "string" || typeof cell === "number"
        ? normalizeText(cell)
        : "";

    return valor.includes(filtro);
  });
}

/** ---------------------- HTTP helpers ---------------------- */

async function headVersao(
  moduloAtual: ModuloAtual | null,
  signal?: AbortSignal
): Promise<number | null> {
  try {
    if (!moduloAtual?.id) return null;

    const params = new URLSearchParams();
    params.set("modulo_id", String(moduloAtual.id));

    const url = `${URL_ESTOQUE}?${params.toString()}`;

    const res = await fetch(url, {
      method: "HEAD",
      headers: buildAuthHeaders(false),
      signal,
    });

    if (!res.ok) return null;

    const v = res.headers.get("x-cache-ver");
    const n = v ? Number(v) : null;

    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

async function getLista(
  moduloAtual: ModuloAtual | null,
  signal?: AbortSignal
): Promise<{
  fonte: "cache" | "banco" | undefined;
  versao: number | null;
  data: EstoqueProduto[];
  response: ListaResp | null;
}> {
  if (!moduloAtual?.id) {
    throw new Error("Módulo de estoque não identificado para esta rota");
  }

  const res = await fetch(URL_ESTOQUE, {
    method: "POST",
    headers: buildAuthHeaders(true),
    cache: "no-store",
    signal,
    body: JSON.stringify({
      modulo_id: moduloAtual.id,
    }),
  });

  const parsedJson = (await res.json().catch(() => null)) as ListaResp | null;

  if (!res.ok || parsedJson?.ok === false) {
    const erro =
      (parsedJson as any)?.erro ||
      (Array.isArray((parsedJson as any)?.erros) &&
        (parsedJson as any).erros.join(" | ")) ||
      `Erro HTTP ${res.status}`;

    throw new Error(erro);
  }

  const fonte = (res.headers.get("x-source") as "cache" | "banco") || undefined;

  const vh = res.headers.get("x-cache-ver");
  const verHdr = vh ? Number(vh) : null;

  const verBody =
    parsedJson && typeof parsedJson.versao === "number"
      ? parsedJson.versao
      : null;

  const versao = Number.isFinite(verHdr as number) ? verHdr : verBody;

  const dataRaw: EstoqueRowAPI[] =
    parsedJson && Array.isArray(parsedJson.visiveis)
      ? parsedJson.visiveis
      : parsedJson && Array.isArray(parsedJson.data)
      ? parsedJson.data
      : [];

  const data = dataRaw.filter((row) => row?.ocultar !== true).map(normalizeRow);

  return {
    fonte,
    versao: versao ?? null,
    data,
    response: parsedJson,
  };
}

/** ---------------------- Hook viewport ---------------------- */

function useViewportWidth() {
  const [width, setWidth] = useState<number>(() =>
    typeof window === "undefined" ? 1280 : window.innerWidth
  );

  useEffect(() => {
    const update = () => setWidth(window.innerWidth);

    update();

    window.addEventListener("resize", update);

    return () => window.removeEventListener("resize", update);
  }, []);

  return width;
}

/** ---------------------- Hook genérico ---------------------- */

function useEstoqueDisponivel(
  moduloAtual: ModuloAtual | null,
  authLoading: boolean
) {
  const [itens, setItens] = useState<EstoqueProduto[]>([]);
  const [loading, setLoading] = useState(true);
  const [fonte, setFonte] = useState<"cache" | "banco" | undefined>(undefined);
  const [versao, setVersao] = useState<number | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [meta, setMeta] = useState<ListaResp | null>(null);

  const versaoRef = useRef<number | null>(null);
  const stop = useRef(false);
  const fetching = useRef(false);

  const setVersaoBoth = (v: number | null) => {
    versaoRef.current = v;
    setVersao(v);
  };

  async function carregar(signal?: AbortSignal) {
    fetching.current = true;

    try {
      setErro(null);

      const result = await getLista(moduloAtual, signal);

      setFonte(result.fonte);
      setVersaoBoth(result.versao);
      setMeta(result.response);

      setItens((prev) => {
        if (sameArray(prev, result.data)) return prev;
        return result.data;
      });
    } catch (e: any) {
      if (e?.name !== "AbortError") {
        setErro(e?.message || "Erro ao carregar estoque.");
        throw e;
      }
    } finally {
      fetching.current = false;
    }
  }

  useEffect(() => {
    stop.current = false;
    setLoading(true);
    setErro(null);
    setItens([]);
    setMeta(null);
    setVersaoBoth(null);

    const bootstrapController = new AbortController();

    if (authLoading) {
      return () => {
        stop.current = true;
        bootstrapController.abort();
      };
    }

    if (!moduloAtual?.id) {
      setLoading(false);
      setErro("Módulo de estoque não identificado para esta rota.");

      return () => {
        stop.current = true;
        bootstrapController.abort();
      };
    }

    (async () => {
      try {
        const head = await headVersao(moduloAtual, bootstrapController.signal);

        if (head !== null) {
          setVersaoBoth(head);
        }

        await carregar(bootstrapController.signal);
        setLoading(false);

        while (!stop.current) {
          while (fetching.current && !stop.current) {
            await sleep(25);
          }

          if (stop.current) break;

          await sleep(POLL_FALLBACK_MS);

          if (stop.current) break;

          await carregar();
        }
      } catch (e) {
        if (!stop.current) {
          console.error("useEstoqueDisponivel loop error:", e);
        }

        setLoading(false);
      }
    })();

    return () => {
      stop.current = true;
      bootstrapController.abort();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduloAtual?.id, authLoading]);

  const refresh = async () => {
    while (fetching.current) {
      await sleep(25);
    }

    await carregar();
  };

  return {
    itens,
    loading,
    fonte,
    versao,
    erro,
    meta,
    refresh,
  };
}

/** ---------------------- Página genérica ---------------------- */

const EstoqueGenerico: React.FC = () => {
  const { areaSlug, estoqueSlug } = useParams<{
    areaSlug: string;
    estoqueSlug: string;
  }>();

  const { sessao, loading: authLoading } = useAuth();

  const viewportWidth = useViewportWidth();
  const isTablet = viewportWidth <= TABLET_BREAKPOINT;
  const isMobile = viewportWidth <= MOBILE_BREAKPOINT;
  const isSmallMobile = viewportWidth <= SMALL_MOBILE_BREAKPOINT;

  const config = useMemo(
    () => getConfigByParams(areaSlug, estoqueSlug),
    [areaSlug, estoqueSlug]
  );

  const moduloAtual = useMemo(
    () => findModuloAtualFromSessao(sessao, config),
    [sessao, config]
  );

  const { itens, loading, erro, meta } = useEstoqueDisponivel(
    moduloAtual,
    authLoading
  );

  const pageLoading = authLoading || loading;

  const [modoDiretoria, setModoDiretoria] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailCodigo, setDetailCodigo] = useState<string | null>(null);

  const abrirDetalheProduto = (codigo: string) => {
    const codigoLimpo = String(codigo || "").trim();

    if (!codigoLimpo) return;

    setDetailCodigo(codigoLimpo);
    setDetailOpen(true);
  };

  const fecharDetalheProduto = () => {
    setDetailOpen(false);
    setDetailCodigo(null);
  };

  const title = useMemo(() => {
    const estoqueNome = meta?.estoque?.nome;
    const moduloLabel = meta?.modulo?.label || moduloAtual?.label;

    if (estoqueNome) return `${slugToTitle(config.areaSlug)} - ${estoqueNome}`;
    if (moduloLabel) return `${slugToTitle(config.areaSlug)} - ${moduloLabel}`;

    return config.title;
  }, [config.areaSlug, config.title, meta, moduloAtual]);

  const headers = useMemo(
    () => [
      "Posição",
      "Código",
      "NCM",
      "Nome do Produto",
      "Custo Unitário",
      "Moeda",
      "Quantidade",
      "Classificação",
    ],
    []
  );

  const headersDiretoria = useMemo(
    () => [
      "Código",
      "Nome do Produto",
      "Q.",
      "Custo Unitário",
      "Moeda",
      "Margem (%)",
      "Preço de Venda",
      "Vendável",
      "Classificação",
      "Posição",
    ],
    []
  );

  const columnWidths = useMemo(() => {
    if (isSmallMobile) {
      return ["0px", "72px", "0px", "210px", "92px", "0px", "68px", "0px"];
    }

    if (isMobile) {
      return ["0px", "78px", "0px", "260px", "98px", "0px", "72px", "0px"];
    }

    if (isTablet) {
      return ["58px", "90px", "0px", "360px", "108px", "0px", "82px", "0px"];
    }

    return [
      "72px",
      "110px",
      "110px",
      "420px",
      "130px",
      "80px",
      "95px",
      "130px",
    ];
  }, [isSmallMobile, isMobile, isTablet]);

  const columnWidthsDiretoria = useMemo(
    () => [
      "100px",
      "300px",
      "50px",
      "120px",
      "80px",
      "110px",
      "130px",
      "80px",
      "120px",
      "90px",
    ],
    []
  );

  const responsiveWidths = useMemo(
    () => ({
      1300: {
        1: "64px",
        2: "100px",
        3: "100px",
        4: "380px",
        5: "120px",
        6: "72px",
        7: "86px",
        8: "120px",
      },
      1024: {
        1: "58px",
        2: "90px",
        3: "0px",
        4: "360px",
        5: "108px",
        6: "0px",
        7: "82px",
        8: "0px",
      },
      768: {
        1: "0px",
        2: "82px",
        3: "0px",
        4: "300px",
        5: "102px",
        6: "0px",
        7: "76px",
        8: "0px",
      },
      520: {
        1: "0px",
        2: "74px",
        3: "0px",
        4: "230px",
        5: "96px",
        6: "0px",
        7: "70px",
        8: "0px",
      },
      420: {
        1: "0px",
        2: "70px",
        3: "0px",
        4: "205px",
        5: "90px",
        6: "0px",
        7: "64px",
        8: "0px",
      },
    }),
    []
  );

  const responsiveWidthsDiretoria = useMemo(
    () => ({
      1300: {
        1: "100px",
        2: "260px",
        3: "55px",
        4: "110px",
        5: "70px",
        6: "95px",
        7: "120px",
        8: "80px",
        9: "110px",
        10: "80px",
      },
      1024: {
        1: "95px",
        2: "240px",
        3: "55px",
        4: "105px",
        5: "70px",
        6: "90px",
        7: "115px",
        8: "80px",
        9: "100px",
        10: "75px",
      },
      768: {
        1: "90px",
        2: "220px",
        3: "50px",
        4: "105px",
        5: "70px",
        6: "88px",
        7: "110px",
        8: "75px",
        9: "95px",
        10: "70px",
      },
    }),
    []
  );

  const [mostrarZerados, setMostrarZerados] = useState(false);
  const [revelarZeradosNosFiltros, setRevelarZeradosNosFiltros] =
    useState(false);

  const [tableFilters, setTableFilters] = useState<string[]>(
    headers.map(() => "")
  );

  const [selectedColumns] = useState<boolean[]>(headers.map(() => true));

  const enabledColumnIndexes = useMemo(
    () =>
      selectedColumns
        .map((checked, i) => (checked ? i : -1))
        .filter((i) => i !== -1),
    [selectedColumns]
  );

  const filtrosAtivos = useMemo(
    () => tableFilters.some((f) => normalizeText(f) !== ""),
    [tableFilters]
  );

  useEffect(() => {
    if (!filtrosAtivos) {
      setRevelarZeradosNosFiltros(false);
    }
  }, [filtrosAtivos]);

  const itensComRows = useMemo(
    () =>
      itens.map((item) => ({
        item,
        row: buildRow(item),
      })),
    [itens]
  );

  const itensFiltradosPelosCamposDaTabela = useMemo(() => {
    return itensComRows.filter(({ row }) =>
      rowMatchesFilters(row, tableFilters)
    );
  }, [itensComRows, tableFilters]);

  const deveMostrarZerados =
    mostrarZerados || (filtrosAtivos && revelarZeradosNosFiltros);

  const itensVisiveis = useMemo(() => {
    if (deveMostrarZerados) {
      return itensFiltradosPelosCamposDaTabela.map(({ item }) => item);
    }

    return itensFiltradosPelosCamposDaTabela
      .map(({ item }) => item)
      .filter((p) => (p.quantidade ?? 0) > 0);
  }, [itensFiltradosPelosCamposDaTabela, deveMostrarZerados]);

  const rows = useMemo(() => itensVisiveis.map(buildRow), [itensVisiveis]);

  const rowsDiretoria = useMemo(
    () => itensVisiveis.map(buildDiretoriaRow),
    [itensVisiveis]
  );

  const existeSomenteZeradoNosFiltros = useMemo(() => {
    if (!filtrosAtivos || deveMostrarZerados) return false;

    const encontrados = itensFiltradosPelosCamposDaTabela.map(
      ({ item }) => item
    );

    if (!encontrados.length) return false;

    const comSaldo = encontrados.some((p) => (p.quantidade ?? 0) > 0);
    const zerados = encontrados.some((p) => (p.quantidade ?? 0) <= 0);

    return !comSaldo && zerados;
  }, [filtrosAtivos, deveMostrarZerados, itensFiltradosPelosCamposDaTabela]);

  const quantidadeItensZeradosEncontrados = useMemo(() => {
    if (!filtrosAtivos || deveMostrarZerados) return 0;

    return itensFiltradosPelosCamposDaTabela.filter(
      ({ item }) => (item.quantidade ?? 0) <= 0
    ).length;
  }, [filtrosAtivos, deveMostrarZerados, itensFiltradosPelosCamposDaTabela]);

  const itensRelatorio = useMemo(
    () => itens.filter((p) => (p.quantidade ?? 0) > 0),
    [itens]
  );

  const rowsReport = useMemo(
    () => itensRelatorio.map(buildRow),
    [itensRelatorio]
  );

  const totalProdutosRel = useMemo(
    () => itensRelatorio.reduce((acc, p) => acc + (p.quantidade ?? 0), 0),
    [itensRelatorio]
  );

  const totaisPorMoeda = useMemo(() => {
    const acc: Record<string, number> = {};

    itensRelatorio.forEach((p) => {
      const moeda = moedaFromItem(p);
      if (!moeda) return;

      const custo = Number(String(p.custoUnitario ?? 0).replace(",", "."));
      const qtd = p.quantidade ?? 0;
      const subtotal = Number.isFinite(custo * qtd) ? custo * qtd : 0;

      if (!acc[moeda]) acc[moeda] = 0;
      acc[moeda] += subtotal;
    });

    return acc;
  }, [itensRelatorio]);

  const footerLines = useMemo(() => {
    const lines: string[] = [];

    lines.push(`Total de Produtos no Estoque: ${totalProdutosRel}`);

    const entries = Object.entries(totaisPorMoeda);
    if (!entries.length) return lines;

    entries.sort(([a], [b]) => a.localeCompare(b));

    entries.forEach(([moeda, total]) => {
      const symbol = moedaSymbol(moeda);

      const formatted = `${symbol ? symbol + " " : ""}${total.toLocaleString(
        "pt-BR",
        {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }
      )}`;

      const extensoMoeda = numeroMonetarioParaExtenso(total, moeda);

      lines.push(
        `Valor Total em Estoque (${moeda}): ${formatted} (${extensoMoeda})`
      );
    });

    return lines;
  }, [totalProdutosRel, totaisPorMoeda]);

  const gerarRelatorioEstoque = () => {
    generatePdfReport({
      reportTitle: meta?.estoque?.nome
        ? `Relatório de Estoque - ${meta.estoque.nome}`
        : config.reportTitle,
      fileNamePrefix: config.fileNamePrefix,
      subtitle: "Informações dos Produtos no Estoque",
      headers,
      rows: rowsReport,
      custoColIndex: 4,
      enabledColumns: enabledColumnIndexes,
      footerLines,
    });
  };

  useEffect(() => {
    const handler = (event: Event) => {
      const custom = event as CustomEvent<{
        actionKey?: string;
        label?: string;
        codigo?: string | null;
        modulo?: {
          id?: number | string | null;
          label?: string;
          codigo?: string | null;
          slug?: string;
        };
        departamento?: {
          id?: number | string | null;
          name?: string;
          slug?: string | null;
          routeKey?: string;
        };
        pathContext?: {
          pathname?: string;
          selectedAddressValue?: string;
        };
      }>;

      if (custom.detail?.actionKey !== "generate_report") return;

      const currentPath = window.location.pathname.replace(/\/+$/, "");
      const basePath = `/${config.areaSlug}/${config.estoqueSlug}`;

      const pertenceAoEstoqueAtual =
        currentPath === basePath ||
        currentPath === `${basePath}/estoque` ||
        currentPath.startsWith(`${basePath}/`);

      if (!pertenceAoEstoqueAtual) return;

      gerarRelatorioEstoque();
    };

    window.addEventListener("logvi:menu-action", handler);

    return () => {
      window.removeEventListener("logvi:menu-action", handler);
    };
  }, [config, headers, rowsReport, enabledColumnIndexes, footerLines, meta]);

  const handleMostrarZeradosSomenteNosFiltros = () => {
    setRevelarZeradosNosFiltros(true);
  };

  const getCellStyleDiretoria = (
    _viewIndex: number,
    _colIndex: number,
    _value: string | number | React.ReactNode,
    originalIndex: number,
    _rowData: (string | number | React.ReactNode)[],
    headerTitle: string
  ) => {
    const p = itensVisiveis[originalIndex];
    if (!p) return undefined;

    const GREEN_PASTEL = "#e8f7ec";
    const RED_PASTEL = "#fdeaea";

    if (headerTitle === "Vendável") {
      if (isVendavelSim(p.vendavel)) return { background: GREEN_PASTEL };
      if (isVendavelNao(p.vendavel)) return { background: RED_PASTEL };
      return undefined;
    }

    if (headerTitle === "Margem (%)") {
      const margem = Number(
        String(p.margemPercentual ?? "0").replace(",", ".")
      );

      if (!Number.isFinite(margem)) return undefined;

      return {
        background: margem < 10 ? RED_PASTEL : GREEN_PASTEL,
      };
    }

    if (headerTitle === "Preço de Venda") {
      const margem = Number(
        String(p.margemPercentual ?? "0").replace(",", ".")
      );

      if (!Number.isFinite(margem) || margem === 0) {
        return { background: RED_PASTEL };
      }
    }

    return undefined;
  };

  const activeHeaders = modoDiretoria ? headersDiretoria : headers;
  const activeRows = modoDiretoria ? rowsDiretoria : rows;
  const activeColumnWidths = modoDiretoria
    ? columnWidthsDiretoria
    : columnWidths;
  const activeResponsiveWidths = modoDiretoria
    ? responsiveWidthsDiretoria
    : responsiveWidths;

  const activeCodigoIdx = activeHeaders.indexOf("Código");

  const activeMinWidth = modoDiretoria
    ? isMobile
      ? 1120
      : 1080
    : isSmallMobile
    ? 545
    : isMobile
    ? 610
    : isTablet
    ? 710
    : "100%";

  return (
    <div
      style={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        overflowX: "hidden",
        padding: isMobile ? "0 10px 18px" : isTablet ? "0 14px 22px" : 0,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
        }}
      >
        <TitleButtons fullBleedLine title={title} />
      </div>

      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 10,
          margin: "0 0 12px",
          boxSizing: "border-box",
        }}
      >
        <button
          type="button"
          onClick={() => setModoDiretoria((prev) => !prev)}
          style={{
            height: 34,
            padding: "0 14px",
            border: "1px solid #cbd5e1",
            background: modoDiretoria ? "#111827" : "#ffffff",
            color: modoDiretoria ? "#ffffff" : "#1f2937",
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.02em",
            whiteSpace: "nowrap",
            boxSizing: "border-box",
          }}
        >
          {modoDiretoria ? "Ver Estoque Normal" : "View Diretoria"}
        </button>
      </div>

      {!modoDiretoria && existeSomenteZeradoNosFiltros && (
        <div
          style={{
            marginBottom: 12,
            padding: isMobile ? "10px 10px" : "10px 12px",
            border: "1px solid #e7cf9a",
            background: "#fff8e8",
            color: "#6b4f1f",
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "stretch" : "center",
            gap: isMobile ? 10 : 12,
            flexWrap: "wrap",
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              fontSize: isMobile ? 12.5 : 13,
              fontWeight: 500,
              lineHeight: 1.45,
              minWidth: 0,
            }}
          >
            Você possui este item ou similar com quantidade 0.
            {quantidadeItensZeradosEncontrados > 1
              ? ` Foram encontrados ${quantidadeItensZeradosEncontrados} itens zerados.`
              : ""}{" "}
            Clique em "Mostrar Itens Zerados" para exibir.
          </div>

          <button
            type="button"
            onClick={handleMostrarZeradosSomenteNosFiltros}
            style={{
              height: 34,
              width: isMobile ? "100%" : "auto",
              padding: "0 14px",
              border: "1px solid #c9ad66",
              background: "#ffffff",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 700,
              textTransform: "uppercase",
              color: "#6b4f1f",
              whiteSpace: "nowrap",
              boxSizing: "border-box",
            }}
          >
            Mostrar Itens Zerados
          </button>
        </div>
      )}

      {erro && (
        <div
          style={{
            marginBottom: 12,
            padding: isMobile ? "10px 10px" : "10px 12px",
            border: "1px solid #efb4b4",
            background: "#fff1f1",
            color: "#7f1d1d",
            fontSize: isMobile ? 12.5 : 13,
            fontWeight: 600,
            lineHeight: 1.45,
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            boxSizing: "border-box",
            overflowWrap: "anywhere",
          }}
        >
          {erro}
        </div>
      )}

      {pageLoading ? (
        <div
          style={{
            padding: isMobile ? "14px 4px" : 16,
            fontSize: isMobile ? 13 : 14,
            color: "#334155",
          }}
        >
          Carregando estoque…
        </div>
      ) : (
        <div
          style={{
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            boxSizing: "border-box",
            overflowX: "auto",
            overflowY: "visible",
            WebkitOverflowScrolling: "touch",
            paddingBottom: isMobile ? 8 : 0,
          }}
        >
          <div
            style={{
              width: "100%",
              minWidth: activeMinWidth,
              boxSizing: "border-box",
            }}
          >
            <Table
              headers={activeHeaders}
              rows={activeRows}
              columnWidths={activeColumnWidths}
              responsiveWidths={activeResponsiveWidths}
              {...(!modoDiretoria
                ? {
                    filters: tableFilters,
                    onFiltersChange: setTableFilters,
                  }
                : {})}
              enableCellColors={modoDiretoria}
              getCellStyle={modoDiretoria ? getCellStyleDiretoria : undefined}
              onDoubleClick={(_viewIndex, rowData) => {
                const codigo = String(rowData[activeCodigoIdx] ?? "").trim();

                if (codigo) {
                  abrirDetalheProduto(codigo);
                }
              }}
            />
          </div>
        </div>
      )}

      <ProdutoDetailSidePanel
        open={detailOpen}
        codigo={detailCodigo}
        moduloAtual={moduloAtual}
        areaTitle={title}
        onClose={fecharDetalheProduto}
      />
    </div>
  );
};

export default EstoqueGenerico;
